import { useState, useEffect, useRef } from 'react';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

/**
 * Async-checks citation URLs for dead links via the backend proxy.
 *
 * Calls POST /api/citations/check after citations render.
 * The backend HEAD-checks each URL, hides dead chunks, and
 * creates an operator task — this hook only cares about the
 * dead_urls list so the UI can gray them out.
 *
 * Fails silently — this is an enhancement, not a critical path.
 */
export function useCitationHealth(citations, clientId, requestId) {
  const [deadUrls, setDeadUrls] = useState(new Set());
  const checkedRef = useRef(null);

  useEffect(() => {
    if (!citations?.length || !clientId || !requestId) return;
    if (checkedRef.current === requestId) return;
    checkedRef.current = requestId;

    const payload = {
      client_id: clientId,
      request_id: requestId,
      citations: citations.map((c) => ({
        chunk_id: c.chunk_id,
        url: c.url,
        title: c.title,
      })),
    };

    fetch(`${API_BASE_URL}/api/citations/check`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
      .then((r) => r.json())
      .then((data) => {
        if (data.dead_urls?.length) {
          console.warn('[CitationHealth]', data.dead_urls.length, 'dead citation(s) detected');
          setDeadUrls(new Set(data.dead_urls));
        }
      })
      .catch(() => {
        // Fail silently — dead link detection is best-effort
      });

    // No cleanup — checkedRef prevents duplicate fetches,
    // and we don't want StrictMode's unmount/remount to cancel
    // the in-flight request.
  }, [citations, clientId, requestId]);

  return { deadUrls };
}
